import { useFormik } from "formik";
import Skeleton from "react-loading-skeleton";
import { ModalComponent } from "..";
import {
  useCreateCooperativeRoleMutation,
  useEditCooperativeRoleMutation,
  useGetCooperativePermissionsQuery,
} from "../../../redux/features/settings/settings.api";
import { SingleRole } from "../../../redux/features/settings/settings.type";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../utils/constants";
import { manageRoleSchema } from "../../../validationSchema";
import { Icon } from "../../icons/icon";
import { Checkbox } from "../../inputs/checkbox";
import { TextInput } from "../../inputs/textInput";

export const ManageRoleModal = ({
  isModalOpen,
  onModalClose,
  successCallback = () => {},
  role,
}: {
  isModalOpen: boolean;
  onModalClose: () => void;
  successCallback?: (role: SingleRole) => void;
  role?: SingleRole;
}) => {
  const [createRole, { isLoading: creatingRole }] =
    useCreateCooperativeRoleMutation();
  const [editRole, { isLoading: editingRole }] =
    useEditCooperativeRoleMutation();

  const { data: permissions, isFetching: fetchingPermissions } =
    useGetCooperativePermissionsQuery(undefined, {
      refetchOnMountOrArgChange: false,
      skip: !isModalOpen,
    });

  const {
    values,
    errors,
    isValid,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    setFieldValue,
    resetForm,
  } = useFormik({
    initialValues: {
      title: role?.Name ?? "",
      permissions: role?.permissions?.map((v) => v.CodeName) ?? [],
    } as {
      title: string;
      permissions: string[];
    },
    enableReinitialize: true,
    validationSchema: manageRoleSchema,
    onSubmit: async (data) => {
      if (!!role) {
        const resp = await editRole({
          title: data.title,
          permission: data.permissions,
          roleId: role.id,
        });

        if (resp.data?.success) {
          resetForm();
          onModalClose();
          successCallback(resp.data.data);
        }
      } else {
        const resp = await createRole({
          title: data.title,
          permission: data.permissions,
        });

        if (resp.data?.success) {
          resetForm();
          onModalClose();
          successCallback(resp.data.data);
        }
      }
    },
  });
  return (
    <>
      <ModalComponent
        isModalOpen={isModalOpen}
        onModalClose={() => {
          resetForm();
          onModalClose();
        }}
        title={!!role ? "Edit role" : "Create role"}
        subTitle={`Kindly ${
          !!role ? "update" : "name"
        } the role and pick the different accesses for the role.`}
        childrenClassName=""
        modalClassName=""
        hasButtons
        proceedButtonText={!!role ? "Save Changes" : "Create Role"}
        onProceedClick={handleSubmit}
        hasCancelButton={false}
        isLoading={creatingRole || editingRole}
        isProceedDisabled={
          fetchingPermissions || creatingRole || editingRole || !isValid
        }
      >
        <div className="flex flex-col gap-4">
          <TextInput
            type="text"
            name="title"
            value={values.title}
            placeholder="Give a title to the role"
            onChange={handleChange}
            onBlur={handleBlur}
            hasIcon={false}
            iconSvg={undefined}
            errors={errors?.title && touched?.title ? errors?.title : ""}
            title="Name of Role"
            hasTitle
            autoComplete="off"
          />

          <div className="flex flex-col gap-2">
            <p className="text-pry-dark-green-300 font-medium text-sm">
              Select accesses
            </p>

            {fetchingPermissions
              ? [1, 2, 3, 4].map((i) => (
                  <Skeleton
                    width={"100%"}
                    height={"20px"}
                    baseColor={SKELETON_BASE_COLOR}
                    highlightColor={SKELETON_HIGHLIGHT_COLOR}
                    className="rounded-md"
                    key={`${i}`}
                  />
                ))
              : undefined}

            {!fetchingPermissions
              ? permissions?.data?.map((permission) => {
                  const tooltipId = `permission-tooltip-${permission.code}`;

                  return (
                    <div
                      key={permission.code}
                      className="flex flex-row items-center justify-between"
                    >
                      <Checkbox
                        checked={values.permissions.includes(permission.code)}
                        onCheck={() => {
                          setFieldValue("permissions", [
                            ...values.permissions,
                            permission.code,
                          ]);
                        }}
                        onUnCheck={() => {
                          setFieldValue(
                            "permissions",
                            values.permissions.filter(
                              (v) => v !== permission.code
                            )
                          );
                        }}
                        text={permission.Title}
                        className="text-sm text-pry-green-400 "
                      />

                      <Icon
                        svg="help-circle"
                        width={16}
                        height={16}
                        id={tooltipId}
                        className="text-[#A4A7AE]"
                        data-tooltip-id="tooltip"
                        data-tooltip-content={permission.Title}
                      />
                    </div>
                  );
                })
              : undefined}
          </div>
        </div>
      </ModalComponent>
    </>
  );
};
